<template>
  <UIPopup
    v-model="modelValue"
    :title="t('listings.filtersPopup.title')"
    :button-text="t('common.actions.apply')"
    :close-text="t('listings.filtersPopup.reset')"
    size="extraBig"
    :secondary-button-closes="false"
    max-width="1380px"
    closeable
    @close="emit('update:modelValue', false)"
  >
    <template #header>
      <div class="text-headline-4 flex justify-start">{{ t('listings.filtersPopup.title') }}</div>
    </template>
    <template #body>
      <div class="grid grid-cols-1 gap-3 overflow-hidden lg:grid-cols-2 lg:gap-12 xl:grid-cols-3">
        <div class="flex flex-col gap-8">
          <UIDropdown
            id="emirate"
            v-model="localFilters.emirate_names"
            :items="emirateNames"
            name="emirateNames"
            :loading="isLoadingEmirateNames"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="t('listings.filtersPopup.emirateLabel')"
            :placeholder="t('listings.filtersPopup.emirateLabel')"
            persistent-placeholder
          />
          <UIDropdown
            id="location_ids"
            v-model="localFilters.location_ids"
            :items="locations"
            name="districts"
            :loading="isLoadingLocations"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="t('listings.filtersPopup.cityDistrictsLabel')"
            :placeholder="t('listings.filtersPopup.cityDistrictsLabel')"
            persistent-placeholder
          />
          <UIDropdown
            id="apartment-types"
            v-model="localFilters.apartment_type"
            :items="apartmentTypes"
            :loading="isLoadingApartmentTypes"
            name="apartmentTypes"
            label-key="name"
            value-key="id"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="t('listings.filtersPopup.propertyTypeLabel')"
            :placeholder="t('listings.filtersPopup.propertyTypeLabel')"
            persistent-placeholder
          />

          <div class="flex flex-row items-end gap-4">
            <UIInputText
              id="area-from"
              v-model="localFilters.area_min"
              name="area-from"
              :label="t('listings.filtersPopup.propertyAreaLabel')"
              :placeholder="t('listings.filtersPopup.fromPlaceholder')"
              :suffix="areaSuffix"
              sync-v-model
              :controlled="false"
              compact
              :data-maska="'#'.repeat(9)"
            />

            <span class="mb-3">-</span>
            <UIInputText
              id="area-to"
              v-model="localFilters.area_max"
              name="area-to"
              :placeholder="t('listings.filtersPopup.toPlaceholder')"
              :suffix="areaSuffix"
              sync-v-model
              :controlled="false"
              compact
              :data-maska="'#'.repeat(9)"
            />
          </div>
          <UIDropdown
            v-model="localFilters.amenities"
            :items="amenities"
            :loading="isLoadingAmenities"
            multiple
            compact
            :label="t('listings.filtersPopup.amenitiesLabel')"
            name="Amenities"
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :placeholder="t('listings.filtersPopup.amenitiesLabel')"
            persistent-placeholder
          />
        </div>
        <div class="flex flex-col gap-8">
          <UIInputOptions
            id="stage"
            v-model="localFilters.building_status"
            name="stage"
            :items="buildingStatusesFormatted"
            label-key="name"
            value-key="id"
            :loading="isLoadingBuildingStatuses"
            :label="t('listings.filtersPopup.constructionStagesLabel')"
          />
          <UIInputOptions
            id="bedrooms"
            v-model="localFilters.bedrooms"
            name="bedrooms"
            :items="bedroomItems"
            :label="t('listings.filtersPopup.bedroomsLabel')"
            multiple
          />
          <div class="flex flex-row items-center gap-4">
            <UIInputText
              id="budget-min"
              v-model="localFilters.price_min"
              name="budget-min"
              :label="t('listings.filtersPopup.minBudgetLabel')"
              :data-maska="'#'.repeat(9)"
              :prefix="t('listings.filtersPopup.fromAedPrefix')"
              input-class="pl-[85px]"
              sync-v-model
              :controlled="false"
              compact
            />
            <UIInputText
              id="budget"
              v-model="localFilters.price_max"
              name="budget"
              :label="t('listings.filtersPopup.maxBudgetLabel')"
              :data-maska="'#'.repeat(9)"
              :prefix="t('listings.filtersPopup.upToAedPrefix')"
              input-class="pl-[95px]"
              sync-v-model
              :controlled="false"
              compact
            />
          </div>
          <UIInputText
            id="down-payment"
            v-model="localFilters.down_payment"
            name="down-payment"
            :label="t('listings.filtersPopup.downPaymentLabel')"
            :data-maska="'#'.repeat(9)"
            :prefix="t('listings.filtersPopup.upToAedPrefix')"
            input-class="pl-[95px]"
            sync-v-model
            :controlled="false"
            compact
          />
          <UIDropdown
            v-model="localFilters.sale_status"
            :items="salesStatuses"
            :loading="isLoadingSalesStatuses"
            multiple
            compact
            :label="t('listings.filtersPopup.salesStatusLabel')"
            name="sales-status"
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :placeholder="t('common.actions.any')"
            persistent-placeholder
          />
        </div>
        <div class="flex flex-col gap-8">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row items-center gap-2">
              <UIInputText
                id="floor-from"
                name="floor-from"
                :placeholder="t('listings.filtersPopup.floorFromPlaceholder')"
                :label="t('listings.filtersPopup.floorLabel')"
                disabled
                model-value=""
                compact
              />
              <UIInputText
                id="floor-to"
                name="floor-to"
                :placeholder="t('listings.filtersPopup.floorToPlaceholder')"
                :label="t('listings.filtersPopup.floorLabel')"
                disabled
                model-value=""
                compact
              />
            </div>
            <UICheckbox
              v-model="localFilters.floor_plan"
              name="floorPlanAvailable"
              sync-v-model
              :controlled="false"
            >
              {{ t('listings.filtersPopup.floorPlanAvailableLabel') }}
            </UICheckbox>
          </div>
          <UICheckbox
            v-model="localFilters.post_handover_payment_plan"
            sync-v-model
            :controlled="false"
            name="postHandoverPaymentPlan"
            label=""
          >
            {{ t('listings.filtersPopup.postHandoverPaymentPlanLabel') }}
          </UICheckbox>
          <!-- <UICheckbox v-model="localFilters.roi_percent" sync-v-model :controlled="false" name="guaranteedROI">
            Guaranteed ROI
          </UICheckbox> -->
        </div>
      </div>
    </template>
    <template #buttons>
      <slot name="buttons">
        <UIButton
          id="popup-close"
          compact
          variant="secondary"
          @click="reset"
        >
          {{ t('listings.filtersPopup.reset') }}
        </UIButton>
        <UIButton
          id="popup-apply"
          compact
          class="popup__content-buttons-apply"
          @click="applyFilters"
        >
          {{ t('common.actions.apply') }}
        </UIButton>
      </slot>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep';
import { BEDROOMS_ITEMS } from '~/constants/properties';
import type { Filters } from '~/types/properties';

const emit = defineEmits(['update:modelValue', 'cancel', 'input', 'reset']);

const modelValue = defineModel<boolean>({ required: true });

const MENU_HEIGHT = '270px';

type Props = {
  filters: Filters;
};

const props = defineProps<Props>();

const { t } = useI18n();

// BEDROOMS_ITEMS `value`s are the stored filter values; only the "Any"/"Studio" text is translated.
const bedroomItems = computed(() =>
  BEDROOMS_ITEMS.map((item) => {
    if (item.value === null) return { ...item, text: t('common.actions.any') };
    if (item.value === 0) return { ...item, text: t('common.priceTable.studio') };
    return item;
  }),
);
const { areaSuffix } = useAreaFormat();
const localFilters = ref<Filters>({ ...cloneDeep(props.filters) });
// Re-read the bar's filters on every open so Apply never writes back stale toggles (e.g. the 100% Commission chip).
watch(modelValue, (isOpen) => {
  if (isOpen) localFilters.value = { ...cloneDeep(props.filters) };
});

const { data: apartmentTypes, isLoading: isLoadingApartmentTypes } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.APARTMENT_TYPE,
);
const { data: locations, isLoading: isLoadingLocations } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.LOCATIONS);
const { data: amenities, isLoading: isLoadingAmenities } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.AMENITIES);
const { data: emirateNames, isLoading: isLoadingEmirateNames } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.EMIRATE_NAMES,
);

const { data: buildingStatuses, isLoading: isLoadingBuildingStatuses } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.BUILDING_STATUSES,
);

const buildingStatusesFormatted = computed(() => {
  return [
    {
      id: null,
      name: t('common.actions.any'),
    },
    ...(buildingStatuses.value || []),
  ];
});

const { data: rawSalesStatuses, isLoading: isLoadingSalesStatuses } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.SALE_STATUSES,
);

// #49 — collapse Postgres status_code values into 4 canonical UI labels:
// "Pre launch" / "EOI Stage" / "On Sale" / "Sold out". The dictionary now
// returns one row per status_code; each bucket aggregates the relevant codes.
// When the agent ticks one canonical label, all underlying codes are
// submitted as filter values so the server's `sale_status = ANY(...)`
// matches every project in that bucket.
const CANONICAL_SALE_STATUS_BUCKETS: { labelKey: string; codes: string[] }[] = [
  { labelKey: 'listings.filtersPopup.saleStatus.preLaunch', codes: ['LAUNCH', 'PRELAUNCH'] },
  { labelKey: 'listings.filtersPopup.saleStatus.eoiStage', codes: ['EOI'] },
  { labelKey: 'listings.filtersPopup.saleStatus.onSale', codes: ['ON_SALE'] },
  { labelKey: 'listings.filtersPopup.saleStatus.soldOut', codes: ['SOLD_OUT'] },
];

// Dropdown items — one per canonical bucket. `id` is the first underlying
// status_code (UIDropdown expects an `id` field); `_allIds` keeps every code
// the bucket covers so applyFilters() expands the selection before submit.
// `name` is the localized DISPLAY label only — the actual filter values sent
// to the server are the underlying `code`/`_allIds` (never the label).
const salesStatuses = computed(() => {
  const raw = (rawSalesStatuses.value ?? []) as Array<{ id: string | number; name?: string; code?: string }>;
  const buckets: { id: string; name: string; code: string; _allIds: string[] }[] = [];
  for (const bucket of CANONICAL_SALE_STATUS_BUCKETS) {
    const matching = raw.filter((d) => bucket.codes.includes(String(d.code || d.id || '').toUpperCase()));
    if (matching.length === 0) continue;
    const ids = matching.map((m) => String(m.code || m.id));
    buckets.push({
      id: ids[0],
      name: t(bucket.labelKey),
      code: matching[0].code ?? ids[0],
      _allIds: ids,
    });
  }
  return buckets;
});

// Outbound expansion: a canonical bucket pick → every status_code it covers.
const expandSaleStatusSelection = (selectedIds: string[]): string[] => {
  const expanded = new Set<string>();
  for (const id of selectedIds) {
    const bucket = salesStatuses.value.find((b) => b.id === id);
    if (bucket) bucket._allIds.forEach((x) => expanded.add(x));
    else expanded.add(id);
  }
  return Array.from(expanded);
};

const reset = () => {
  localFilters.value = {
    query: '',
    property_type_ids: [],
    sale_status: [],
    bedrooms: [],
    budget: [],
    order_by: '',
    order_way: '',
    apartment_type: [],
    amenities: [],
    location_ids: [],
    emirate_names: [],
    developer_names: [],
    building_status: undefined,
    area_min: undefined,
    area_max: undefined,
    price_min: undefined,
    price_max: undefined,
    down_payment: undefined,
    floor_min: undefined,
    floor_max: undefined,
    floor_plan: false,
    post_handover_payment_plan: false,
    exclusive: false,
    favorites_only: false,
    developer_vault_only: false,
  };

  emit('reset');
};

const applyFilters = () => {
  const outgoing = {
    ...localFilters.value,
    sale_status: expandSaleStatusSelection(localFilters.value.sale_status ?? []),
  };
  emit('input', outgoing);

  modelValue.value = false;
};
</script>

<style scoped></style>
